/**
 * Dashboard "details" (name + description) commit path for
 * DashboardSettingsDialog (S24 review).
 *
 * The dialog's `onRename` prop can't carry a description, and running two
 * sequential saves would race their revisions — so when the description
 * changed, BOTH fields go out in ONE full-document save. Replays on conflict
 * exactly like `useDashboardActions.saveWithReplay`: the main-process
 * repository is the only writer, and a concurrent MCP mutation must not
 * silently swallow the user's edit.
 */
import { toast } from '@/components/ui/toaster';
import type { Dashboard } from '../types/dashboardTypes';
import { useDashboardStore } from '../store/dashboardStore';

const MAX_SAVE_REPLAYS = 3;

/** An imported definition could carry junk here — never render a non-string. */
export const readDashboardDescription = (dashboard: Dashboard): string =>
  typeof dashboard.description === 'string' ? dashboard.description : '';

export async function saveDashboardDetails(
  dashboard: Dashboard,
  name: string,
  description: string | undefined,
): Promise<void> {
  let target = dashboard;
  for (let attempt = 0; attempt < MAX_SAVE_REPLAYS; attempt++) {
    const result = await useDashboardStore
      .getState()
      .save({ ...target, name, description }, target.revision);
    if (result.ok) {
      // Success-side analytics (repo rule); non-React context → bridge call.
      window.electronAPI?.analytics
        ?.track('feature:dashboard_description_save', { hasDescription: description !== undefined })
        .catch(() => {});
      return;
    }
    if (result.error !== 'conflict') {
      toast.error({ title: 'Could not save the dashboard details', description: result.detail });
      return;
    }
    const fresh = useDashboardStore.getState().dashboards.find((d) => d.id === target.id);
    if (!fresh) return; // deleted concurrently — nothing to replay onto
    target = fresh;
  }
  toast.error({
    title: 'Could not save the dashboard details',
    description: 'The dashboard kept changing while saving. Try again.',
  });
}
